/* eslint-disable i18next/no-literal-string */
import React, { useMemo, useRef, useState } from 'react'
import { Image, View, StyleSheet, ImageSourcePropType } from 'react-native'
import RootStore, { useStore } from '../../../../../store'
import { BankIcon } from '../../../../../assets/icons'
import { ListPlaceholder } from '../../../../../ui/atoms/ListPlaceholder'
import SwipableListItem from '../../../../../ui/molecules/SwipableListItem'
import { observer } from 'mobx-react-lite'
import noBankImage from '../../../../../assets/images/builder2.0/MoreWallet.svg'
import { Spacer } from '../../../../../ui/atoms'
import { ScrollView } from 'react-native-gesture-handler'
import { useTranslation } from 'react-i18next'
import { BtText } from '@linqpal/components/src/ui'
import { ListItem } from '../../../../../ui/atoms/builder-2.0/ListItem'
import { OnlineConnectedBadge } from '../../../../../ui/atoms/OnlineConnectedBadge'
import { IBankAccountModel, IUIBankAccount } from '@linqpal/models'
import { FinicityConnectionError } from '../FinicityConnectionError'
import {
  PlaidConnectButton,
  PlaidConnectButtonRef,
} from '../../../../../ui/add-payment-method-components/PlaidConnectButton'

interface BankAccountsProps {
  onPress?: (arg: IBankAccountModel) => void
}

interface ItemProps {
  bankAccount: IUIBankAccount
  index: number
  setShowConnectionError?: (arg: boolean) => void
  onPress?: (arg: IBankAccountModel) => void
  createToken?: (arg?: string, arg2?: string) => void
}

export const BankAccounts: React.FC<BankAccountsProps> = React.memo(
  observer(function BankAccounts({ onPress }) {
    const { t } = useTranslation('global')
    const ref = useRef<null | PlaidConnectButtonRef>(null)
    const { screensStore } = useStore()
    const { isBanksEmpty, onlineConnectedBanks, manuallyAddedBanks } =
      screensStore.paymentMethodsStore

    const createToken = (bankId?: string, routingNumber?: string) =>
      ref.current?.createToken(bankId, routingNumber)

    return (
      <ScrollView style={{ backgroundColor: 'white' }}>
        {isBanksEmpty ? (
          <NoBanksAdded />
        ) : (
          <>
            {onlineConnectedBanks.map((bankAccount, index) => (
              <Item
                bankAccount={bankAccount}
                index={index}
                key={bankAccount.id}
                createToken={createToken}
                onPress={onPress}
              />
            ))}
            {!!manuallyAddedBanks.length && (
              <>
                <BtText style={styles.manuallyAdded}>
                  {t('Wallet.manually-added-bank')}
                </BtText>
                {manuallyAddedBanks.map((bankAccount, index) => (
                  <Item
                    bankAccount={bankAccount}
                    index={index}
                    key={bankAccount.id}
                    onPress={onPress}
                  />
                ))}
              </>
            )}
            <PlaidConnectButton ref={ref} />
          </>
        )}
        <Spacer height={50} />
      </ScrollView>
    )
  }),
)

const Item: React.FC<ItemProps> = ({
  bankAccount,
  index,
  createToken,
  onPress: onPressItem,
}) => {
  const { t } = useTranslation('global')
  const { getAccTypeAndNumber } = RootStore.screensStore.paymentMethodsStore
  const [showConnectionError, setShowConnectionError] = useState(false)
  const {
    _id,
    isManualEntry,
    accountType,
    routingNumber,
    accountNumber,
    isReconnectionRequired,
    isFinicityConnectedBank,
    isDisconnectedPlaidBank,
    isExpiredPlaidBank,
  } = bankAccount
  const [selectedId, setSelectedId] = useState('')

  const reconnectionRequired = useMemo(
    () => !onPressItem && isReconnectionRequired, // onPressItem exists only when rendered from ChoosePaymentModal - while paying for an invoice, payment will go through even if connection is broken
    [isReconnectionRequired, onPressItem],
  )
  const onPress = () => {
    setSelectedId(_id)
    if (onPressItem) {
      onPressItem(bankAccount)
    } else if (isFinicityConnectedBank && setShowConnectionError)
      setShowConnectionError(true)
    else if (isExpiredPlaidBank) {
      createToken && createToken(_id)
    } else if (isDisconnectedPlaidBank) {
      createToken && createToken('', routingNumber)
    }
  }
  return (
    <>
      <SwipableListItem key={index}>
        <ListItem
          key={index}
          icon={<BankIcon style={{ height: 40, width: 40 }} />}
          endIcon={<View />}
          badge={
            isManualEntry ? undefined : (
              <OnlineConnectedBadge
                reconnectionRequired={reconnectionRequired}
              />
            )
          }
          title={bankAccount.name?.split('/')[0]}
          onPress={() => onPress()}
          subtitle={getAccTypeAndNumber(accountType, accountNumber, t)}
          otherStyles={{
            title: {
              lineHeight: 26,
              marginTop: isManualEntry ? 0 : 5,
            },
          }}
          style={
            reconnectionRequired
              ? {
                  backgroundColor: 'rgba(219, 8, 28, 0.1)',
                  borderColor: '#DB081C',
                }
              : {}
          }
        />
      </SwipableListItem>
      <FinicityConnectionError
        visible={showConnectionError && selectedId === _id}
        onClose={() => setShowConnectionError(false)}
        finicityBankId={bankAccount.id}
      />
    </>
  )
}

const NoBanksAdded = () => {
  const { t } = useTranslation('global')
  return (
    <ListPlaceholder
      image={
        <Image
          style={{
            width: 202,
            height: 152,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
          source={noBankImage as ImageSourcePropType}
        />
      }
      heading={t('Wallet.no_bank_conected')}
      subHeading={t('Wallet.tap_the_plus')}
    />
  )
}

const styles = StyleSheet.create({
  manuallyAdded: {
    fontWeight: '500',
    fontSize: 15,
    lineHeight: 34,
    color: '#335C75',
    marginBottom: 8,
    marginTop: 10,
    marginLeft: 9,
  },
})
